"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { useCallback } from "react";
import { X, SlidersHorizontal } from "lucide-react";

type Category = {
  id: string;
  name: string;
};

type AgendaFiltersProps = {
  categories: Category[];
  totalResults: number;
};

export default function AgendaFilters({
  categories,
  totalResults,
}: AgendaFiltersProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  // Lecture des filtres actifs depuis l'URL
  const activeCats = searchParams.get("cat")?.split(",").filter(Boolean) ?? [];
  const activeFrom = searchParams.get("from") ?? "";
  const activeTo = searchParams.get("to") ?? "";

  const hasFilters = activeCats.length > 0 || activeFrom || activeTo;

  // Construit et pousse une nouvelle URL avec les params mis à jour
  const push = useCallback(
    (params: Record<string, string | string[] | null>) => {
      const next = new URLSearchParams(searchParams.toString());

      for (const [key, value] of Object.entries(params)) {
        if (!value || (Array.isArray(value) && value.length === 0)) {
          next.delete(key);
        } else if (Array.isArray(value)) {
          next.set(key, value.join(","));
        } else {
          next.set(key, value);
        }
      }

      router.push(`/agenda?${next.toString()}`, { scroll: false });
    },
    [router, searchParams]
  );

  // Toggle d'une catégorie
  const toggleCategory = (id: string) => {
    const next = activeCats.includes(id)
      ? activeCats.filter((c) => c !== id)
      : [...activeCats, id];
    push({ cat: next });
  };

  // Reset tous les filtres
  const reset = () => router.push("/agenda", { scroll: false });

  return (
    <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <SlidersHorizontal size={16} className="text-slate-400" />
          Filtrer les événements
        </div>

        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400">
            {totalResults} résultat{totalResults !== 1 ? "s" : ""}
          </span>
          {hasFilters && (
            <button
              onClick={reset}
              className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
            >
              <X size={12} />
              Tout effacer
            </button>
          )}
        </div>
      </div>

      {/* ── Catégories ── */}
      {categories.length > 0 && (
        <div className="mb-4">
          <p className="mb-2.5 text-xs font-medium uppercase tracking-wide text-slate-400">
            Catégories
          </p>
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => {
              const active = activeCats.includes(cat.id);
              return (
                <button
                  key={cat.id}
                  onClick={() => toggleCategory(cat.id)}
                  className={[
                    "rounded-full border px-3.5 py-1.5 text-sm font-medium transition",
                    active
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-400 hover:text-slate-900",
                  ].join(" ")}
                >
                  {cat.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Période ── */}
      <div>
        <p className="mb-2.5 text-xs font-medium uppercase tracking-wide text-slate-400">
          Période
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label
              htmlFor="filter-from"
              className="text-sm text-slate-500 whitespace-nowrap"
            >
              Du
            </label>
            <input
              id="filter-from"
              type="date"
              value={activeFrom}
              onChange={(e) => push({ from: e.target.value || null })}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 outline-none transition focus:border-slate-400"
            />
            {activeFrom && (
              <button
                onClick={() => push({ from: null })}
                className="rounded p-1 text-slate-400 hover:text-slate-600"
              >
                <X size={14} />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <label
              htmlFor="filter-to"
              className="text-sm text-slate-500 whitespace-nowrap"
            >
              Au
            </label>
            <input
              id="filter-to"
              type="date"
              value={activeTo}
              min={activeFrom || undefined}
              onChange={(e) => push({ to: e.target.value || null })}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 outline-none transition focus:border-slate-400"
            />
            {activeTo && (
              <button
                onClick={() => push({ to: null })}
                className="rounded p-1 text-slate-400 hover:text-slate-600"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Résumé des filtres actifs ── */}
      {hasFilters && (
        <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
          {activeCats.map((id) => {
            const cat = categories.find((c) => c.id === id);
            if (!cat) return null;
            return (
              <span
                key={id}
                className="flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700"
              >
                {cat.name}
                <button
                  onClick={() => toggleCategory(id)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X size={11} />
                </button>
              </span>
            );
          })}
          {(activeFrom || activeTo) && (
            <span className="flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
              {activeFrom && activeTo
                ? `${activeFrom} → ${activeTo}`
                : activeFrom
                ? `À partir du ${activeFrom}`
                : `Jusqu'au ${activeTo}`}
              <button
                onClick={() => push({ from: null, to: null })}
                className="text-slate-400 hover:text-slate-600"
              >
                <X size={11} />
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
}
